import { FC, FocusEvent, KeyboardEvent, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import type { NavItem } from './AsideNav'
import { NavSubLink } from './NavSubLink'

const VIEWPORT_MARGIN = 8

/** Id do painel no DOM: o ícone aponta para ele (`aria-controls`) e o checa no blur. */
export const flyoutDomId = (entryId: string) => `lz-nav-flyout-${entryId}`

type Props = {
  entryId: string
  title: string
  // Âncora: retângulo do ícone no trilho (o painel abre à direita dele)
  anchor: DOMRect
  // Grupo: lista de subitens navegável. Sem itens: só o rótulo (tooltip) do link solto.
  items?: NavItem[]
  activeTo?: string
  focusFirst?: boolean
  onMouseEnter: () => void
  onMouseLeave: () => void
  // Fecha devolvendo o foco ao ícone (Esc, Tab para fora)
  onDismiss: () => void
  // Foco saiu do painel para outro ponto da página
  onFocusLeave: () => void
}

/**
 * Painel do menu recolhido. Renderizado em portal com `position: fixed` porque o aside tem
 * `overflow: hidden` (e a lista rola), o que cortaria qualquer painel posicionado dentro dele.
 */
const AsideNavFlyout: FC<Props> = ({
  entryId,
  title,
  anchor,
  items,
  activeTo,
  focusFirst,
  onMouseEnter,
  onMouseLeave,
  onDismiss,
  onFocusLeave,
}) => {
  const panelRef = useRef<HTMLDivElement>(null)
  const isTooltip = !items
  const [top, setTop] = useState(isTooltip ? anchor.top + anchor.height / 2 : anchor.top)

  // Painel de grupo que passaria do rodapé da tela sobe até caber
  useLayoutEffect(() => {
    if (isTooltip || !panelRef.current) return
    const height = panelRef.current.offsetHeight
    const maxTop = window.innerHeight - VIEWPORT_MARGIN - height
    setTop(Math.max(VIEWPORT_MARGIN, Math.min(anchor.top, maxTop)))
  }, [anchor.top, isTooltip])

  useEffect(() => {
    if (!focusFirst || !panelRef.current) return
    const links = panelRef.current.querySelectorAll<HTMLAnchorElement>('a')
    const activeLink = panelRef.current.querySelector<HTMLAnchorElement>('a[aria-current="page"]')
    ;(activeLink ?? links[0])?.focus()
  }, [focusFirst])

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const links = Array.from(panelRef.current?.querySelectorAll<HTMLAnchorElement>('a') ?? [])
    const index = links.indexOf(document.activeElement as HTMLAnchorElement)

    if (event.key === 'Escape' || event.key === 'ArrowLeft') {
      event.preventDefault()
      onDismiss()
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      links[(index + 1) % links.length]?.focus()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      links[(index - 1 + links.length) % links.length]?.focus()
    } else if (event.key === 'Tab') {
      // O portal fica no fim do body: sair pelas pontas devolve o foco ao ícone, em vez de
      // pular para o fim da página.
      const leavingForward = !event.shiftKey && index === links.length - 1
      const leavingBackward = event.shiftKey && index === 0
      if (leavingForward || leavingBackward) {
        event.preventDefault()
        onDismiss()
      }
    }
  }

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget as Node | null
    if (!next || !panelRef.current?.contains(next)) onFocusLeave()
  }

  return createPortal(
    <div
      ref={panelRef}
      id={flyoutDomId(entryId)}
      className={clsx('lz-nav-flyout', { 'lz-nav-flyout-tooltip': isTooltip })}
      style={{ top, left: anchor.right + VIEWPORT_MARGIN }}
      // O link solto já tem `aria-label`; o rótulo flutuante é só visual
      aria-hidden={isTooltip || undefined}
      role={isTooltip ? undefined : 'group'}
      aria-label={isTooltip ? undefined : title}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyDown={isTooltip ? undefined : handleKeyDown}
      onBlur={isTooltip ? undefined : handleBlur}
    >
      {isTooltip ? (
        title
      ) : (
        <>
          <div className='lz-nav-flyout-title' aria-hidden='true'>
            {title}
          </div>
          <ul className='lz-nav-flyout-list'>
            {items.map((item) => (
              <NavSubLink
                key={item.to}
                to={item.to}
                title={item.title}
                isActive={item.to === activeTo}
              />
            ))}
          </ul>
        </>
      )}
    </div>,
    document.body
  )
}

export { AsideNavFlyout }
